// src/pages/QuranAcademyWebsite.js
import React, { useState, useEffect } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  ArrowUp,
  MessageCircle,
  Send,
  Calendar,
  BookOpen,
  Users,
  Star
} from "lucide-react";

import Newsletter from "../components/Newsletter";
import Footer from "../components/Footer";
import q1 from "../assets/q1.jpg";
import q2 from "../assets/q2.jpg";
import tvqlogo from "../assets/tvqlogo.png"; // ensure this exists

const QuranAcademyWebsite = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  // initialize with 'home' so the top section is visible immediately
  const [visibleSections, setVisibleSections] = useState(new Set(["home"]));

  // contact form state
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [isSending, setIsSending] = useState(false);

  // newsletter state
  const [newsletterEmail, setNewsletterEmail] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
      const sections = document.querySelectorAll("section[id]");
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
          setVisibleSections((prev) => {
            if (prev.has(section.id)) return prev;
            const next = new Set(prev);
            next.add(section.id);
            return next;
          });
        }
      });
    };
    window.addEventListener("scroll", handleScroll);
    // run once on mount to pick up any sections already in view
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ---------- Contact form submit (works for local dev and production) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();

    // client-side validation (trim to avoid whitespace-only)
    const name = (formData.name || "").trim();
    const email = (formData.email || "").trim();
    const phone = (formData.phone || "").trim();
    const message = (formData.message || "").trim();

    if (!name || !email || !message) {
      alert("Please fill name, email and message.");
      return;
    }

    setIsSending(true);

    // auto-select endpoint: use localhost path when running locally, otherwise production domain
    const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    let endpoint = isLocal
      ? "http://localhost/tvqacademy/contact.php"
      : "https://www.thevirtualquranacademy.com/contact.php";

    endpoint = endpoint.trim(); // avoid stray newline characters

    const payload = { name, email, phone, message };

    // helpful console logging for debugging local failures
    console.debug("Contact submit -> endpoint:", endpoint);
    console.debug("Contact submit -> payload:", payload);

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(payload),
      });

      // attempt parse JSON, log raw text if parse fails
      let data = {};
      const raw = await res.text();
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch (err) {
        console.warn("Failed to parse JSON response, raw:", raw);
      }

      console.debug("Contact submit -> status:", res.status, "response:", data);

      if (!res.ok) {
        // show server-provided message if any
        alert(data?.error || `Server error (${res.status}). Try again later.`);
        return;
      }

      if (data?.success) {
        alert(data.message || "Message sent — thank you!");
        setFormData({ name: "", email: "", phone: "", message: "" });
      } else {
        // server returned success=false with reason
        alert(data?.error || "Failed to send message.");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      alert("Network error. Please check your connection and try again.");
    } finally {
      setIsSending(false);
    }
  };

  // Newsletter submit (simple)
  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    alert("Thank you for subscribing to our newsletter!");
    setNewsletterEmail("");
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) {
      // fallback: open top
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // scroll smoothly
    el.scrollIntoView({ behavior: "smooth", block: "start" });

    // ensure the section is marked visible (so it's not stuck opacity-0)
    setVisibleSections((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    // close mobile menu
    setIsMenuOpen(false);

    // optional: after a tiny delay, re-run a scroll handler to update others
    setTimeout(() => {
      const ev = new Event("scroll");
      window.dispatchEvent(ev);
    }, 300);
  };

  const menuItems = [
    { label: "Home", id: "home" },
    { label: "About", id: "about" },
    { label: "Teachers", id: "teachers" },
    { label: "Classes", id: "classes" },
    { label: "Events", id: "events" },
    { label: "Blogs", id: "blogs" },
    { label: "Contact", id: "contact" }
  ];

  const teachers = [
    { name: "Hafiz Muhammad Ali", qualification: "Quran Hafiz & Islamic Scholar", experience: "10+ Years Experience", icon: <Users className="w-16 h-16 text-green-700" /> },
    { name: "Qari Abdul Rahman", qualification: "Tajweed Expert & Qari", experience: "8+ Years Experience", icon: <Users className="w-16 h-16 text-green-700" /> },
    { name: "Dr. Fatima Ahmed", qualification: "Islamic Studies PhD", experience: "12+ Years Experience", icon: <Users className="w-16 h-16 text-green-700" /> },
    { name: "Hafiz Omar Khan", qualification: "Arabic & Quran Teacher", experience: "6+ Years Experience", icon: <Users className="w-16 h-16 text-green-700" /> }
  ];

  const classesList = [
    { title: "Quran Recitation", description: "Learn proper pronunciation and recitation of the Holy Quran with Tajweed rules", image: "https://images.unsplash.com/photo-1609599006353-e629aaabfeae?w=400&h=250&fit=crop", duration: "6 Months" },
    { title: "Quran Memorization", description: "Comprehensive Hifz program for memorizing the complete Holy Quran", image: q1, duration: "2-4 Years" },
    { title: "Arabic Language", description: "Master Arabic language to understand Quran and Islamic texts better", image: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=250&fit=crop", duration: "1 Year" },
    { title: "Islamic Studies", description: "Comprehensive Islamic education covering Hadith, Fiqh, and Islamic history", image: q2, duration: "8 Months" }
  ];

  const events = [
    { title: "Ramadan Special Classes", description: "Intensive Quran recitation and spiritual development classes during the holy month", icon: <Calendar className="w-6 h-6" /> },
    { title: "Eid ul-Fitr Celebration", description: "Special programs and competitions for students celebrating Eid", icon: <Star className="w-6 h-6" /> },
    { title: "Eid ul-Adha Program", description: "Educational sessions about Hajj and sacrifice with special activities", icon: <BookOpen className="w-6 h-6" /> },
    { title: "Laylat al-Qadr Night", description: "Special night prayers and Quran recitation sessions", icon: <Users className="w-6 h-6" /> }
  ];

  const blogs = [
    { title: "Benefits of Learning Quran Online", excerpt: "Discover how online Quran education is transforming Islamic learning worldwide", image: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400&h=250&fit=crop"},
    { title: "Importance of Tajweed in Quran", excerpt: "Understanding why proper pronunciation is crucial in Quranic recitation", image: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&h=250&fit=crop"},
    { title: "Teaching Quran to Children", excerpt: "Effective methods and approaches for introducing Quran to young learners", image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=250&fit=crop"},
    { title: "Memorizing Quran: A Complete Guide", excerpt: "Step-by-step guidance for successful Quran memorization journey", image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=250&fit=crop"}
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-gradient-to-r from-green-800 via-green-700 to-green-800 backdrop-blur-sm shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="hidden md:flex space-x-8">
              {menuItems.slice(0, 3).map((item) => (
                <button key={item.id} onClick={() => scrollToSection(item.id)} className="text-white hover:text-yellow-300 px-4 py-2 text-lg font-bold transition-all duration-300 hover:scale-110 relative group">
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
                </button>
              ))}
            </div>

            {/* Mobile menu button */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2 text-white hover:text-yellow-300 z-60">
              {isMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
            </button>

            <div className="bg-white rounded-full p-4 shadow-xl border-4 border-yellow-400 
  hover:border-yellow-300 transition-all duration-300 hover:scale-110 mt-12">
              <img
                src={tvqlogo}
                alt="Virtual Quran Academy"
                className="h-20 w-20 rounded-full object-cover"
              />
            </div>

            <div className="hidden md:flex space-x-8">
              {menuItems.slice(3).map((item) => (
                <button key={item.id} onClick={() => scrollToSection(item.id)} className="text-white hover:text-yellow-300 px-4 py-2 text-lg font-bold transition-all duration-300 hover:scale-110 relative group">
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
                </button>
              ))}
            </div>
          </div>

          {isMenuOpen && (
            <div className="md:hidden absolute top-full left-0 right-0 bg-gradient-to-b from-green-800 to-green-900 border-t-2 border-yellow-400 shadow-2xl">
              <div className="px-4 pt-4 pb-6 space-y-2">
                {menuItems.map((item) => (
                  <button key={item.id} onClick={() => scrollToSection(item.id)} className="block w-full text-left px-4 py-3 text-lg font-bold text-white hover:text-yellow-300 hover:bg-green-700 transition-all duration-300 rounded-lg transform hover:scale-105">
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section id="home" className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat transform scale-110 animate-pulse" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1584286595398-a59f21d312d4?w=1920&h=1080&fit=crop)', backgroundAttachment: 'fixed', animation: 'float 6s ease-in-out infinite' }} />
          <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 via-green-800/70 to-emerald-900/80" />
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="absolute bg-yellow-400 rounded-full opacity-20 animate-bounce" style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, width: `${Math.random() * 8 + 4}px`, height: `${Math.random() * 8 + 4}px`, animationDelay: `${Math.random() * 3}s`, animationDuration: `${Math.random() * 3 + 2}s` }} />
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center justify-center h-full px-4">
          <div className="text-center text-white max-w-5xl animate-pulse">
            <div className="absolute inset-0 flex items-center justify-center opacity-10 z-0">
              <img src="https://images.unsplash.com/photo-1609599006353-e629aaabfeae?w=800&h=600&fit=crop" alt="Quran Background" className="max-w-full max-h-full object-contain transform scale-150 opacity-30" />
            </div>

            <div className="relative z-10">
              <h1 className="text-6xl md:text-8xl font-bold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-500 animate-pulse">Welcome to Virtual Quran Academy</h1>
              <p className="text-2xl md:text-3xl mb-10 opacity-95 font-semibold text-shadow-lg animate-pulse">✨ Experience personalized one-on-one Quran classes with certified teachers ✨</p>
              <button onClick={() => scrollToSection("contact")} className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-green-900 px-12 py-6 rounded-full text-xl font-bold transition-all duration-300 transform hover:scale-110 shadow-2xl border-4 border-white">Start Learning Today</button>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) scale(1.05); }
            50% { transform: translateY(-20px) scale(1.1); }
          }
          .text-shadow-lg { text-shadow: 2px 2px 4px rgba(0,0,0,0.5); }
        `}</style>
      </section>

      {/* About */}
      <section id="about" className={`py-20 bg-gray-50 transition-all duration-1000 ${visibleSections.has("about") ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-20"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 delay-200 ${visibleSections.has("about") ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-20"}`}>
            <h2 className="text-4xl font-bold text-green-800 mb-4 animate-pulse">About Virtual Quran Academy</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-yellow-600 mx-auto mb-6 animate-bounce" />
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className={`transition-all duration-1000 delay-400 transform ${visibleSections.has("about") ? "opacity-100 translate-x-0 rotate-0" : "opacity-0 -translate-x-20 rotate-3"}`}>
              <img src="https://images.unsplash.com/photo-1609599006353-e629aaabfeae?w=600&h=400&fit=crop" alt="Quran Learning" className="rounded-lg shadow-2xl w-full hover:scale-105 transition-transform duration-500 border-4 border-yellow-400" />
            </div>
            <div className={`transition-all duration-1000 delay-600 ${visibleSections.has("about") ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-20"}`}>
              <h3 className="text-2xl font-bold text-green-800 mb-4 animate-pulse">Founded with Vision to Make Quranic Education Accessible</h3>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed">Founded with a vision to make Quranic education available to everyone, regardless of location or schedule, our platform offers interactive, one-on-one classes led by highly qualified and experienced Quran teachers.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-lg border border-green-100 hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:rotate-1">
                  <div className="text-yellow-500 mb-2"><BookOpen className="w-8 h-8 animate-bounce" /></div>
                  <h4 className="font-semibold text-green-800 mb-1">Certified Tutors</h4>
                  <p className="text-sm text-gray-600">Qualified and experienced teachers</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-lg border border-green-100 hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:-rotate-1">
                  <div className="text-yellow-500 mb-2"><Users className="w-8 h-8 animate-bounce" /></div>
                  <h4 className="font-semibold text-green-800 mb-1">Personalized Learning</h4>
                  <p className="text-sm text-gray-600">One-on-one interactive sessions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Teachers */}
      <section id="teachers" className={`py-20 bg-white transition-all duration-1000 ${visibleSections.has("teachers") ? "opacity-100 translate-x-0" : "opacity-0 translate-x-20"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 delay-200 ${visibleSections.has("teachers") ? "opacity-100 translate-x-0" : "opacity-0 translate-x-20"}`}>
            <h2 className="text-4xl font-bold text-green-800 mb-4 animate-pulse">Our Qualified Teachers</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-yellow-600 mx-auto mb-6 animate-bounce" />
            <p className="text-gray-600 max-w-2xl mx-auto">Learn from certified and experienced Quran teachers who are dedicated to providing the best Islamic education</p>
          </div>

          <div className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-1000 delay-400 ${visibleSections.has("teachers") ? "opacity-100 translate-x-0" : "opacity-0 translate-x-20"}`}>
            {teachers.map((teacher, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 text-center hover:shadow-2xl transition-all duration-500 border border-green-100 transform hover:scale-105 hover:rotate-1">
                <div className="w-24 h-24 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-full mx-auto mb-4 flex items-center justify-center border-4 border-yellow-500 hover:border-yellow-300 transition-all duration-300">
                  {teacher.icon}
                </div>
                <h3 className="text-xl font-semibold text-green-800 mb-2">{teacher.name}</h3>
                <p className="text-gray-600 mb-2">{teacher.qualification}</p>
                <p className="text-sm text-yellow-600 font-medium">{teacher.experience}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Classes */}
      <section id="classes" className={`py-20 bg-gray-50 transition-all duration-1000 ${visibleSections.has("classes") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-green-800 mb-4 animate-pulse">Our Classes</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-yellow-600 mx-auto mb-6 animate-bounce" />
            <p className="text-gray-600 max-w-2xl mx-auto">Comprehensive Islamic education programs designed for all levels and ages</p>
          </div>

          <div className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-1000 delay-400 ${visibleSections.has("classes") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}>
            {classesList.map((course, index) => (
              <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-green-100 transform hover:scale-105 hover:rotate-1">
                <img
                  src={course.image}
                  alt={course.title}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://via.placeholder.com/800x480?text=Image+Not+Available"; }}
                  className="w-full h-48 object-cover hover:scale-110 transition-transform duration-300"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-green-800 mb-2">{course.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{course.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-yellow-600 font-medium text-sm">Duration: {course.duration}</span>
                    <button className="text-green-700 hover:text-green-900 font-medium text-sm">Learn More →</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events */}
      <section id="events" className={`py-20 bg-white transition-all duration-1000 ${visibleSections.has("events") ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-20"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 delay-200 ${visibleSections.has("events") ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-20"}`}>
            <h2 className="text-4xl font-bold text-green-800 mb-4 animate-pulse">Yearly Events & Special Classes</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-yellow-600 mx-auto mb-6 animate-bounce" />
            <p className="text-gray-600 max-w-2xl mx-auto">Join our special programs during Islamic occasions and celebrations</p>
          </div>

          <div className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-1000 delay-400 ${visibleSections.has("events") ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-20"}`}>
            {events.map((event, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 hover:shadow-2xl transition-all duration-500 border border-green-100 transform hover:scale-105 hover:rotate-1">
                <div className="text-yellow-500 mb-4 flex justify-center">{event.icon}</div>
                <h3 className="text-xl font-semibold text-green-800 mb-2">{event.title}</h3>
                <p className="text-gray-600 mb-4 text-sm">{event.description}</p>
                <p className="text-yellow-600 font-medium text-sm">{event.date}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blogs */}
      <section id="blogs" className={`py-20 bg-gray-50 transition-all duration-1000 ${visibleSections.has("blogs") ? "opacity-100 translate-x-0" : "opacity-0 translate-x-20"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 delay-200 ${visibleSections.has("blogs") ? "opacity-100 translate-x-0" : "opacity-0 translate-x-20"}`}>
            <h2 className="text-4xl font-bold text-green-800 mb-4 animate-pulse">Latest Blogs</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-yellow-600 mx-auto mb-6 animate-bounce" />
            <p className="text-gray-600 max-w-2xl mx-auto">Read our latest articles on Quranic education and Islamic learning</p>
          </div>

          <div className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-1000 delay-400 ${visibleSections.has("blogs") ? "opacity-100 translate-x-0" : "opacity-0 translate-x-20"}`}>
            {blogs.map((blog, index) => (
              <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-green-100 transform hover:scale-105 hover:rotate-1">
                <div className="w-full h-48 bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                  <BookOpen className="w-16 h-16 text-green-700" />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-green-800 mb-2">{blog.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{blog.excerpt}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-xs">{blog.date}</span>
                    <button className="text-green-700 hover:text-green-900 font-medium text-sm">Read More →</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact - reduced bottom padding to close gap */}
      <section id="contact" className={`pt-16 pb-6 bg-white transition-all duration-1000 ${visibleSections.has("contact") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-0">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Contact Us</h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto mb-4"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">Get in touch with us to start your Quranic learning journey today</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-bold text-green-800 mb-6">Send us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea rows="4" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
                </div>
                <button type="submit" disabled={isSending} className={`w-full max-w-md bg-green-800 hover:bg-green-900 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${isSending ? "opacity-70 cursor-wait" : ""}`}>
                  <Send className="w-4 h-4" /> {isSending ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-green-800 mb-6">Get in Touch</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-green-100 p-3 rounded-lg"><Mail className="w-6 h-6 text-green-800" /></div>
                  <div>
                    <h4 className="font-semibold text-green-800">Email</h4>
                    <p className="text-gray-600">info@thevirtualquranacademy.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-green-100 p-3 rounded-lg"><Phone className="w-6 h-6 text-green-800" /></div>
                  <div>
                    <h4 className="font-semibold text-green-800">Phone</h4>
                    <p className="text-gray-600">+92 315 2457297</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-green-100 p-3 rounded-lg"><MessageCircle className="w-6 h-6 text-green-800" /></div>
                  <div>
                    <h4 className="font-semibold text-green-800">WhatsApp</h4>
                    <a href="https://wa.me/971525619185" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-green-700 underline">+971 525 619185</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-green-100 p-3 rounded-lg"><MapPin className="w-6 h-6 text-green-800" /></div>
                  <div>
                    <h4 className="font-semibold text-green-800">Location</h4>
                    <p className="text-gray-600">Karachi, Pakistan</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter visible={visibleSections.has("newsletter")} newsletterEmail={newsletterEmail} setNewsletterEmail={setNewsletterEmail} onSubmit={handleNewsletterSubmit} />

      {/* Footer */}
      <Footer visible={true} scrollToSection={scrollToSection} />

      {/* WhatsApp Button */}
      <a href="https://wa.me/971525619185" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-colors z-50">
        <MessageCircle className="w-6 h-6" />
      </a>

      {/* Scroll to top */}
      {showScrollTop && (
        <button onClick={scrollToTop} className="fixed bottom-20 right-6 bg-gradient-to-r from-yellow-400 to-yellow-600 text-green-900 p-3 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110 z-50 border-2 border-white animate-bounce">
          <ArrowUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default QuranAcademyWebsite;
